import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { oldWebsiteDTO } from '../../models/app';
import {
  AuthAcceptTerms,
  ChangePasswordDTO,
  changePasswordResponseDTO,
  CompanyDTO,
  CreateUserDTO,
  CreateUserNewDTO,
  EmailConfirmDTO,
  EmailConfirmV2DTO,
  MemberEnrollmentDTO,
  MemberEnrollmentRequest,
  MemberEnrollmentResponse,
  ResendEmailDTO,
  UpdateMemberEmailAddressDTO,
} from '../../models/auth';
import { AppConfigService } from './app-config.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  apiName = 'authService';
  webApiName = 'webApi';
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values: any) => values.every((b: any) => b)));

  constructor(private oauthService: OAuthService, private http: HttpClient) {}
  public setAuthConfig(
    additionalParam: string | null,
    additionalParam2: string | null,
    additionalParam3?: string | null
  ): void {
    const authConfig = AppConfigService.settings.authentication;
    if (additionalParam) {
      authConfig.customQueryParams = { code: additionalParam, virtual_path: additionalParam2 };
    }
    if (additionalParam3) {
      authConfig.customQueryParams = { tpa: additionalParam3 };
    }
    this.oauthService.configure(authConfig);

    this.oauthService.events.subscribe((_: any) => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });

    this.oauthService.setupAutomaticSilentRefresh();
  }

  public async runInitialLoginSequence(): Promise<void> {
    return this.oauthService
      .loadDiscoveryDocument()
      .then(() => this.oauthService.tryLoginCodeFlow())
      .then(() => {
        this.isDoneLoadingSubject$.next(true);
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  public revokeTokenAndLogout() {
    this.oauthService.revokeTokenAndLogout().then(() => {
      localStorage.clear();
    });
  }

  public hasRole(role: string) {
    let claims: any = this.oauthService.getIdentityClaims();
    if (claims && claims.groups) {
      let roles: string[] = claims.groups;
      roles = roles.map(role => role.toUpperCase());
      return roles.includes(role.toLocaleUpperCase());
    }
    return false;
  }

  public getFullname() {
    let claims: any = this.oauthService.getIdentityClaims();
    if (claims && claims.name) {
      return claims.name;
    }
    return undefined;
  }

  public getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  public getIdToken() {
    return this.oauthService.getIdToken();
  }

  public login() {
    this.oauthService.initCodeFlow();
  }
  // public logout() {
  //   this.oauthService.logOut();
  // }
  public async logout(): Promise<void> {
    // Wrap logOut in a Promise to make it awaitable
    await new Promise<void>(resolve => {
      this.oauthService.logOut();
      resolve();
    });
  }
  public refresh() {
    this.oauthService.silentRefresh();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }
  get isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }
  refreshToken(): Promise<any> {
    return this.oauthService.refreshToken().then(response => {
      return response;
    });
  }
  async loadUserProfile(): Promise<void> {
    try {
      if (this.oauthService.hasValidAccessToken()) {
        await this.refreshToken(); // Wait for the refreshToken to complete
        this.oauthService.loadUserProfile().then(profile => {
          return profile;
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.login(); // Trigger login on error
    }
  }
  updateUserPassword(updatePassword: ChangePasswordDTO) {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/change-password`;
    return this.http.post<changePasswordResponseDTO>(url_, updatePassword);
  }
  validateCompany(tpaCompanyId: string): Observable<CompanyDTO> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/validate-company/{tpaCompanyId}`;
    url_ = url_.replace('{tpaCompanyId}', tpaCompanyId);
    return this.http.get<CompanyDTO>(url_);
  }
  validateMemberEnrollment(enrollment: MemberEnrollmentDTO): Observable<MemberEnrollmentDTO> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/member-enrollment-info`;
    return this.http.post<MemberEnrollmentDTO>(url_, enrollment);
  }
  validateMember(enrollment: any): Observable<any> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/validate-member`;
    return this.http.post<any>(url_, enrollment);
  }
  createUser(createUser: CreateUserDTO): Observable<boolean> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/create-user`;
    return this.http.post<boolean>(url_, createUser);
  }
  createUserWithOutEmailVerification(createUser: CreateUserNewDTO): Observable<boolean> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/create-user-v2`;
    return this.http.post<boolean>(url_, createUser);
  }
  confirmEmail(emailConfirm: EmailConfirmDTO): Observable<any> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/email-confirm`;
    return this.http.post<any>(url_, emailConfirm);
  }
  confirmEmail_v2(emailConfirm: EmailConfirmV2DTO): Observable<any> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/email-confirm-v2`;
    return this.http.post<any>(url_, emailConfirm);
  }
  resendEmail(resendEmail: ResendEmailDTO): Observable<any> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/resend-email`;
    return this.http.post<any>(url_, resendEmail);
  }
  switchToOldSite(oldWebsite: oldWebsiteDTO) {
    let url_ = `${AppConfigService.webApiURL(this.webApiName)}/SSO/Generate`;
    return this.http.post<any>(url_, oldWebsite);
  }
  acceptTerms(acceptTerms: AuthAcceptTerms): Observable<boolean> {
    let url = AppConfigService.authApiURl(this.apiName) + '/user/acceptterms';
    return this.http.post<boolean>(url, acceptTerms);
  }
  addOrUpdateCompanyPayroll(
    IdCompanyPayrollCalendarMiscData: number,
    idCompany: number,
    DeptID: string,
    PayCycle: string
  ): Observable<boolean> {
    let url = `${AppConfigService.authApiURl(this.apiName)}/user/AddOrUpdateCompanyPayollCalendarMiscData?`;
    url = url + 'IdCompanyPayollCalendarMiscData=' + IdCompanyPayrollCalendarMiscData;
    url = url + '&IdCompany=' + idCompany;
    url = url + '&DeptID=' + DeptID;
    url = url + '&PayCycle=' + PayCycle;

    return this.http.post<boolean>(url, null);
  }
  addUpdateMember(enrollmentRequest: MemberEnrollmentRequest): Observable<MemberEnrollmentResponse> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/AddUpdateMember`;
    return this.http.post<MemberEnrollmentResponse>(url_, enrollmentRequest);
  }
  addEmailAddress(emailAddress: UpdateMemberEmailAddressDTO): Observable<MemberEnrollmentResponse> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/SetupNext`;
    return this.http.post<MemberEnrollmentResponse>(url_, emailAddress);
  }
  updateMemberDemographics(enrollmentRequest: MemberEnrollmentRequest): Observable<MemberEnrollmentResponse> {
    let url_ = `${AppConfigService.authApiURl(this.apiName)}/user/UpdateMemberDemographics`;
    return this.http.post<MemberEnrollmentResponse>(url_, enrollmentRequest);
  }
}
